import { toastNotifications } from '@uag/react-core';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Vector3 } from 'three';

import { SettingModelSettingType, SolveCornerPlacementModelMode } from 'api/models';
import { useSolveCornerPlacement } from 'api/queries/forming/forming';
import {
    getAnchorPart,
    raycastPlane
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { buildSolveCornerPlacementRequest } from 'app/modules/wallforming/components/setting-container/corner-placement/utils/buildSolveCornerPlacementRequest';
import { createPlaneFromCandidatePoints } from 'app/modules/wallforming/components/setting-container/hooks/manualSnapUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

export interface ManualCornerInsertionParams {
    articleId: string;
    articleNumber: string | null;
    poiIndex: number;
}

export const usePrepareManualCornerInsertion = () => {
    const { t } = useTranslation();
    const solveCornerPlacementMutation = useSolveCornerPlacement();

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(
        async ({ articleId, poiIndex }: ManualCornerInsertionParams): Promise<boolean> => {
            const state = usePlacementStore.getState();
            state.setConnectorSnapDataLoading(articleId, true);

            try {
                const response = await solveCornerPlacementMutation.mutateAsync({
                    data: buildSolveCornerPlacementRequest({
                        state,
                        sessionId: sessionguid,
                        systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                        poiIndex,
                        mode: SolveCornerPlacementModelMode.Manual,
                        selectedCornerArticleGuid: articleId
                    })
                });

                if (!response.accepted || !response.candidatePoints || response.candidatePoints.length === 0) {
                    state.clearConnectorSnapData();
                    requestAnimationFrame(() => {
                        toastNotifications.error({
                            title: t('error'),
                            message: `${response.errorCode} - ${response.message}`
                        });
                    });

                    return false;
                }

                const firstAlignment = response.candidatePoints[0]?.alignment;
                const planeNormal = firstAlignment
                    ? new Vector3(firstAlignment.x, firstAlignment.y, firstAlignment.z)
                    : new Vector3(1, 0, 0);

                const manualInsertionPlane = createPlaneFromCandidatePoints(response.candidatePoints, planeNormal);
                if (!manualInsertionPlane) {
                    state.clearConnectorSnapData();
                    usePlacementStore.setState({ waitForPlacementData: false });
                    return false;
                }

                const anchor = response.candidatePoints.reduce(
                    (acc, candidate) => acc.add(candidate.position),
                    new Vector3()
                );
                anchor.multiplyScalar(1 / response.candidatePoints.length);
                raycastPlane.setFromNormalAndCoplanarPoint(manualInsertionPlane.normal, anchor);

                const selectedArticle = state.articles[articleId];
                const selectedInstanceId = Object.keys(selectedArticle?.articleInstances ?? {})[0];
                const selectedInstance = selectedInstanceId
                    ? selectedArticle?.articleInstances[selectedInstanceId]
                    : undefined;
                const liveAnchor = selectedInstance ? getAnchorPart(selectedInstance) : null;

                state.pointerPositionRef.current = (liveAnchor?.position ?? anchor).clone();
                state.setConnectorSnapData(
                    articleId,
                    poiIndex,
                    response.candidatePoints,
                    manualInsertionPlane,
                    SettingModelSettingType.Corners
                );
                usePlacementStore.setState({ waitForPlacementData: false });
                return true;
            } catch {
                state.clearConnectorSnapData();
                usePlacementStore.setState({ waitForPlacementData: false });
                return false;
            }
        },
        [sessionguid, solveCornerPlacementMutation, t]
    );
};
